import { useLayoutEffect, useRef, useState } from 'react'

import { webLogger } from 'utils/logger'

import type { AgentMessage } from 'modules/coaching/types'

type ConversationFileLike =
  | {
      processing_status?: string | null
    }
  | null
  | undefined

type UsePendingFirstPromptStateParameters = {
  conversationId: string | undefined
  conversationFile: ConversationFileLike
  hasPendingUploadForCurrentConversation: boolean
  isMessagesLoading: boolean
  messages: AgentMessage[]
  sendMessage: (text: string) => Promise<void>
  uploading: boolean
}

export const usePendingFirstPromptState = (parameters: UsePendingFirstPromptStateParameters) => {
  const {
    conversationFile,
    hasPendingUploadForCurrentConversation,
    isMessagesLoading,
    messages,
    sendMessage,
    uploading,
  } = parameters

  const pendingPromptReference = useRef('')
  const [pendingConfirmed, setPendingConfirmed] = useState(false)
  const [pendingPrompt, setPendingPrompt] = useState('')

  const isProcessingActive =
    hasPendingUploadForCurrentConversation ||
    uploading ||
    (Boolean(conversationFile) &&
      (conversationFile?.processing_status === 'pending' || conversationFile?.processing_status === 'processing'))

  const hasVideoContext = Boolean(conversationFile)

  useLayoutEffect(() => {
    if (!isProcessingActive) {
      setPendingConfirmed(false)
    }
  }, [isProcessingActive])

  useLayoutEffect(() => {
    if (isProcessingActive || !hasVideoContext || isMessagesLoading || !pendingConfirmed) {
      return
    }

    const queuedPrompt = pendingPromptReference.current.trim()
    if (!queuedPrompt) {
      return
    }

    setPendingConfirmed(false)
    pendingPromptReference.current = ''
    setPendingPrompt('')
    sendMessage(queuedPrompt).catch((error: unknown) => {
      webLogger.error('Failed to send queued prompt', {
        error: error instanceof Error ? error.message : String(error),
      })
    })
  }, [hasVideoContext, isMessagesLoading, isProcessingActive, messages, pendingConfirmed, sendMessage])

  return {
    pendingConfirmed,
    pendingPrompt,
    pendingPromptReference,
    setPendingConfirmed,
    setPendingPrompt,
  }
}
